import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { StepsModule } from 'primeng/steps';
import { PanelModule } from '../../../modules/panel.module';
import { MenuService } from '../../../services/menu.service';
import { PromotionService } from '../../../services/promotion.service';

type PriceEntry = {
  group: string;        // menu item / promotion name (for grouping in the preview)
  category: PriceCategoryKey;
  categoryLabel: string;
  label: string;        // specific price label e.g. "Delivery", "Default"
  target: any;          // the object that actually holds the value
  key: string;          // property name on the target
  owner: any;           // the menu item / promotion that must be saved
  ownerType: 'item' | 'promotion';
  oldValue: number;
  newValue: number;
};

type PriceCategoryKey = 'base' | 'source' | 'halfHalf' | 'options' | 'promotions';

@Component({
  selector: 'brt-price-wizard',
  standalone: true,
  imports: [PanelModule, SelectButtonModule, InputNumberModule, StepsModule],
  templateUrl: './price-wizard.component.html',
  styleUrl: './price-wizard.component.scss'
})
export class PriceWizardComponent {
  basePath = this.route.snapshot.data['basePath'] || '/';
  businessId: string;

  loading = true;
  applying = false;
  step = 0;

  steps = [
    { label: 'Configure' },
    { label: 'Preview' },
    { label: 'Done' }
  ];

  // --- configuration ---------------------------------------------------------
  direction: 'increase' | 'decrease' = 'increase';
  directionOptions = [
    { label: 'Increase', value: 'increase', icon: 'pi pi-arrow-up' },
    { label: 'Decrease', value: 'decrease', icon: 'pi pi-arrow-down' }
  ];
  percent: number = 10;

  categories: { key: PriceCategoryKey; label: string; hint: string; enabled: boolean; }[] = [
    { key: 'base', label: 'Base prices', hint: 'Item prices per order type (Delivery / Pickup / Dine-in)', enabled: true },
    { key: 'source', label: 'Order source extra prices', hint: 'Extra amounts added per order source (POS / Online)', enabled: true },
    { key: 'halfHalf', label: 'Half & Half extra price', hint: 'Extra price charged for half & half items', enabled: true },
    { key: 'options', label: 'Option / add-on prices', hint: 'Prices of item options and their order-source extras', enabled: true },
    { key: 'promotions', label: 'Promotion prices', hint: 'Fixed price & discount-amount promotions (percentage discounts are left unchanged)', enabled: true }
  ];

  round = false;
  roundMode = '0.99';
  roundModes = [
    { label: 'Nearest whole (1.00)', value: '1' },
    { label: 'Nearest 0.50', value: '0.5' },
    { label: 'Nearest 0.10', value: '0.1' },
    { label: 'Nearest 0.05', value: '0.05' },
    { label: 'End in .99', value: '0.99' },
    { label: 'End in .95', value: '0.95' }
  ];

  currency = '£';

  // --- preview ---------------------------------------------------------------
  items: any[] = [];
  promotions: any[] = [];
  entries: PriceEntry[] = [];

  constructor(public router: Router, private route: ActivatedRoute,
    private menuService: MenuService, private promotionService: PromotionService,
    private messageService: MessageService) {
    route.params.subscribe(async params => {
      this.businessId = params['bid'];
      await this.load();
    });
  }

  async load() {
    if (!this.businessId) return;
    this.loading = true;
    const [items, promotions] = await Promise.all([
      this.menuService.searchMenuItem({ BusinessId: this.businessId, Size: 5000 }),
      this.promotionService.searchPromotion({ BusinessId: this.businessId, Size: 5000 }).catch(() => ({ List: [] }))
    ]);
    this.items = items.List || [];
    this.promotions = promotions.List || [];
    this.loading = false;
  }

  get factor(): number {
    const p = Number(this.percent) || 0;
    return this.direction === 'decrease' ? 1 - p / 100 : 1 + p / 100;
  }

  get canContinue(): boolean {
    return Number(this.percent) > 0 && this.categories.some(c => c.enabled);
  }

  private isCategoryEnabled(key: PriceCategoryKey) {
    return this.categories.find(c => c.key === key)?.enabled;
  }
  private categoryLabel(key: PriceCategoryKey) {
    return this.categories.find(c => c.key === key)?.label || '';
  }

  roundValue(v: number): number {
    if (!this.round) return Math.round(v * 100) / 100;
    switch (this.roundMode) {
      case '1': return Math.round(v);
      case '0.5': return Math.round(v * 2) / 2;
      case '0.1': return Math.round(v * 10) / 10;
      case '0.05': return Math.round(v * 20) / 20;
      case '0.99': return v <= 0 ? 0 : Math.ceil(v) - 0.01;
      case '0.95': return v <= 0 ? 0 : Math.ceil(v) - 0.05;
      default: return Math.round(v * 100) / 100;
    }
  }

  private pushEntry(list: PriceEntry[], opts: {
    group: string; category: PriceCategoryKey; label: string;
    target: any; key: string; owner: any; ownerType: 'item' | 'promotion';
  }) {
    const raw = opts.target?.[opts.key];
    if (raw == null || raw === '') return;
    const oldValue = Number(raw);
    if (isNaN(oldValue) || oldValue === 0) return;
    const newValue = this.roundValue(oldValue * this.factor);
    list.push({
      ...opts,
      categoryLabel: this.categoryLabel(opts.category),
      oldValue,
      newValue
    });
  }

  buildPreview() {
    const list: PriceEntry[] = [];

    for (const item of this.items) {
      const name = item.Name || '(unnamed item)';

      if (this.isCategoryEnabled('base') && item.BasePriceByType)
        for (const orderType of Object.keys(item.BasePriceByType))
          this.pushEntry(list, { group: name, category: 'base', label: `Base · ${orderType}`, target: item.BasePriceByType, key: orderType, owner: item, ownerType: 'item' });

      if (this.isCategoryEnabled('source') && item.PriceBySource)
        for (const source of Object.keys(item.PriceBySource))
          this.pushEntry(list, { group: name, category: 'source', label: `Source · ${source}`, target: item.PriceBySource, key: source, owner: item, ownerType: 'item' });

      if (this.isCategoryEnabled('halfHalf'))
        this.pushEntry(list, { group: name, category: 'halfHalf', label: 'Half & Half', target: item, key: 'HalfAndHalfPrice', owner: item, ownerType: 'item' });

      if (this.isCategoryEnabled('options'))
        for (const option of item.Options || [])
          for (const choice of option.Choices || []) {
            const choiceName = option.Name || 'Option';
            for (const price of choice.Prices || []) {
              this.pushEntry(list, { group: name, category: 'options', label: `${choiceName} · Default`, target: price, key: 'Price', owner: item, ownerType: 'item' });
              this.pushEntry(list, { group: name, category: 'options', label: `${choiceName} · Delivery`, target: price, key: 'DeliveryPrice', owner: item, ownerType: 'item' });
              this.pushEntry(list, { group: name, category: 'options', label: `${choiceName} · Pickup`, target: price, key: 'PickupPrice', owner: item, ownerType: 'item' });
              this.pushEntry(list, { group: name, category: 'options', label: `${choiceName} · Dine-in`, target: price, key: 'DineInPrice', owner: item, ownerType: 'item' });
            }
            for (const source of Object.keys(choice.PriceBySource || {}))
              this.pushEntry(list, { group: name, category: 'options', label: `${choiceName} · Source ${source}`.trim(), target: choice.PriceBySource, key: source, owner: item, ownerType: 'item' });
          }
    }

    if (this.isCategoryEnabled('promotions'))
      for (const promo of this.promotions) {
        const price = promo.Price;
        if (!price) continue;
        // Only scale monetary discount types; percentage discounts must stay as-is.
        if (price.DiscountType !== 'FixPrice' && price.DiscountType !== 'DiscountAmount') continue;
        const name = promo.Name || '(unnamed promotion)';
        this.pushEntry(list, { group: name, category: 'promotions', label: `${price.DiscountType} · Default`, target: price, key: 'Value', owner: promo, ownerType: 'promotion' });
        this.pushEntry(list, { group: name, category: 'promotions', label: `${price.DiscountType} · Delivery`, target: price, key: 'DeliveryValue', owner: promo, ownerType: 'promotion' });
        this.pushEntry(list, { group: name, category: 'promotions', label: `${price.DiscountType} · Pickup`, target: price, key: 'PickupValue', owner: promo, ownerType: 'promotion' });
        this.pushEntry(list, { group: name, category: 'promotions', label: `${price.DiscountType} · Dine-in`, target: price, key: 'DineInValue', owner: promo, ownerType: 'promotion' });
      }

    this.entries = list;
  }

  goToPreview() {
    if (!this.canContinue) return;
    this.buildPreview();
    this.step = 1;
  }

  backToConfigure() { this.step = 0; }

  async apply() {
    if (!this.entries.length) return;
    this.applying = true;
    try {
      const changedItems = new Set<any>();
      const changedPromotions = new Set<any>();
      for (const entry of this.entries) {
        entry.target[entry.key] = entry.newValue;
        if (entry.ownerType === 'item') changedItems.add(entry.owner);
        else changedPromotions.add(entry.owner);
      }

      const items = Array.from(changedItems);
      if (items.length) await this.menuService.saveMenuItem(...items);

      const promotions = Array.from(changedPromotions);
      for (const promotion of promotions) await this.promotionService.savePromotion(promotion);

      this.messageService.add({
        severity: 'success', summary: 'Prices updated',
        detail: `${this.entries.length} price(s) across ${items.length} item(s) and ${promotions.length} promotion(s) updated.`
      });
      this.step = 2;
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Update failed', detail: 'Some prices could not be saved. Please try again.' });
    } finally {
      this.applying = false;
    }
  }

  reset() {
    this.step = 0;
    this.entries = [];
    this.load();
  }
}
